import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { useIsMobile } from "@/hooks/useMobile";
import { BarChart3, XCircle, TrendingUp, Clock, Calendar, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useState, CSSProperties } from "react";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";

export default function GymReports() {
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const isMobile = useIsMobile();
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    endDate: format(endOfMonth(new Date()), "yyyy-MM-dd")
  });

  const { data: occupancyReport, isLoading: occupancyLoading } = trpc.gym.reports.occupancy.useQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });

  const { data: cancellationReport, isLoading: cancellationLoading } = trpc.gym.reports.cancellations.useQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });

  // Statistics queries - pass date range for filtering
  const { data: peakHours } = trpc.gym.statistics.peakHours.useQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });
  const { data: usageByDay } = trpc.gym.statistics.usageByDay.useQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });
  const { data: servicePopularity } = trpc.gym.statistics.servicePopularity.useQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });

  const COLORS = ['#7AB51D', '#F7941D', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  const totalCheckIns = peakHours?.reduce((sum, h) => sum + h.count, 0) || 0;
  const peakHour = peakHours && peakHours.length > 0 
    ? peakHours.reduce((max, h) => h.count > max.count ? h : max, peakHours[0])
    : undefined;
  const mostPopularDay = usageByDay && usageByDay.length > 0
    ? usageByDay.reduce((max, d) => d.count > max.count ? d : max, usageByDay[0])
    : undefined;
  const mostPopularService = servicePopularity?.[0];

  const setPresetRange = (preset: string) => {
    const today = new Date();
    switch (preset) {
      case "week":
        setDateRange({
          startDate: format(subDays(today, 7), "yyyy-MM-dd"),
          endDate: format(today, "yyyy-MM-dd")
        });
        break;
      case "month":
        setDateRange({
          startDate: format(startOfMonth(today), "yyyy-MM-dd"),
          endDate: format(endOfMonth(today), "yyyy-MM-dd")
        });
        break;
      case "quarter":
        setDateRange({
          startDate: format(subDays(today, 90), "yyyy-MM-dd"),
          endDate: format(today, "yyyy-MM-dd")
        });
        break;
    }
  };

  // Calculate totals from occupancy report array
  const totalSlots = occupancyReport?.reduce((sum, r) => sum + r.totalSlots, 0) ?? 0;
  const totalCapacity = occupancyReport?.reduce((sum, r) => sum + r.totalCapacity, 0) ?? 0;
  const totalBookings = occupancyReport?.reduce((sum, r) => sum + r.totalBookings, 0) ?? 0;
  const avgOccupancy = totalCapacity > 0 ? (totalBookings / totalCapacity) * 100 : 0;

  // Calculate totals from cancellation report array
  const totalAllBookings = cancellationReport?.reduce((sum, r) => sum + r.totalBookings, 0) ?? 0;
  const totalCancellations = cancellationReport?.reduce((sum, r) => sum + r.totalCancellations, 0) ?? 0;
  const avgCancellationRate = totalAllBookings > 0 ? (totalCancellations / totalAllBookings) * 100 : 0;

  // Auth check
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laden...</div>;
  }

  if (!user || !hasAnyRole(user.roles, ["admin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold tracking-tight text-center">
            Anmeldung erforderlich
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Der Zugriff auf diesen Bereich erfordert Administratorrechte.
          </p>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        {isMobile && (
          <header className="flex h-14 items-center gap-2 border-b px-4">
            <SidebarTrigger />
          </header>
        )}
        <div className="p-6 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Gym Berichte & Statistiken</h1>
            <p className="text-muted-foreground mt-2">
              Auslastung, Stornierungen und Nutzungsmuster im Überblick
            </p>   </div>

          {/* Date Range Filter */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="space-y-2">
                  <Label>Von</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bis</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPresetRange("week")}>
                    Letzte Woche
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPresetRange("month")}>
                    Dieser Monat
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPresetRange("quarter")}>
                    Letztes Quartal
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Occupancy Report */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Auslastung nach Service
              </CardTitle>
              <CardDescription>
                Durchschnittliche Auslastung der Slots im gewählten Zeitraum
              </CardDescription>
            </CardHeader>
            <CardContent>
              {occupancyLoading ? (
                <div className="text-center py-8 text-muted-foreground">Laden...</div>
              ) : !occupancyReport || occupancyReport.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Keine Daten verfügbar</div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid gap-4 md:grid-cols-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground">Ø Auslastung</p>
                      <p className="text-2xl font-bold">
                        {avgOccupancy.toFixed(1)}%
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground">Gesamt Slots</p>
                      <p className="text-2xl font-bold">{totalSlots}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground">Gesamt Buchungen</p>
                      <p className="text-2xl font-bold">{totalBookings}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground">Kapazität genutzt</p>
                      <p className="text-2xl font-bold">
                        {totalBookings} / {totalCapacity}
                      </p>
                    </div>
                  </div>

                  {/* By Service */}
                  <div>
                    <h4 className="font-medium mb-3">Nach Service</h4>
                    <div className="space-y-2">
                      {occupancyReport.map((service) => (
                        <div key={service.serviceId} className="flex items-center gap-4">
                          <span className="flex-1">{service.serviceName}</span>
                          <div className="w-32 bg-muted rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ width: `${Math.min(service.occupancyRate, 100)}%` }}
                            />
                          </div>
                          <span className="w-16 text-right text-sm">
                            {service.occupancyRate.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cancellation Report */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Stornierungen nach Benutzer
              </CardTitle>
              <CardDescription>
                Stornierungsstatistiken im gewählten Zeitraum
              </CardDescription>
            </CardHeader>
            <CardContent>
              {cancellationLoading ? (
                <div className="text-center py-8 text-muted-foreground">Laden...</div>
              ) : !cancellationReport || cancellationReport.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Keine Daten verfügbar</div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground">Ø Stornierungsrate</p>
                      <p className="text-2xl font-bold">
                        {avgCancellationRate.toFixed(1)}%
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground">Stornierungen</p>
                      <p className="text-2xl font-bold">{totalCancellations}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground">Gesamt Buchungen</p>
                      <p className="text-2xl font-bold">{totalAllBookings}</p>
                    </div>
                  </div>

                  {/* By User */}
                  <div>
                    <h4 className="font-medium mb-3">Nach Benutzer</h4>
                    <div className="space-y-2">
                      {cancellationReport.slice(0, 10).map((user) => (
                        <div key={user.userId} className="flex items-center gap-4">
                          <span className="flex-1">{user.userName || user.userEmail || "Unbekannt"}</span>
                          <span className="text-sm text-muted-foreground">
                            {user.totalCancellations} / {user.totalBookings} Stornierungen
                          </span>
                          <span className="w-16 text-right text-sm">
                            {user.cancellationRate.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage Statistics Section */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold tracking-tight mb-6">Nutzungsstatistiken</h2>
            
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Gesamt Check-ins</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalCheckIns}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Alle erfassten Check-ins
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Stoßzeit</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{peakHour?.label || "-"}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {peakHour?.count || 0} Check-ins
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Beliebtester Tag</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{mostPopularDay?.dayName || "-"}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {mostPopularDay?.count || 0} Check-ins
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Top Service</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold truncate">{mostPopularService?.serviceName || "-"}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {mostPopularService?.bookingCount || 0} Buchungen
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Peak Hours Chart */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Stoßzeiten nach Uhrzeit</CardTitle>
                <CardDescription>
                  Anzahl der Check-ins pro Stunde des Tages
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!peakHours || peakHours.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    Keine Check-in Daten verfügbar
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={peakHours}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#7AB51D" name="Check-ins" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-6 md:grid-cols-2 mb-6">
              {/* Usage by Day of Week */}
              <Card>
                <CardHeader>
                  <CardTitle>Nutzung nach Wochentag</CardTitle>
                  <CardDescription>
                    Verteilung der Check-ins über die Woche
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!usageByDay || usageByDay.length === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      Keine Check-in Daten verfügbar
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={usageByDay}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dayName" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#F7941D" name="Check-ins" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Service Popularity */}
              <Card>
                <CardHeader>
                  <CardTitle>Beliebteste Services</CardTitle>
                  <CardDescription>
                    Verteilung der Buchungen nach Service-Typ
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!servicePopularity || servicePopularity.length === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      Keine Buchungsdaten verfügbar
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={servicePopularity}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => entry.serviceName}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="bookingCount"
                        >
                          {servicePopularity?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Service Popularity Table */}
            <Card>
              <CardHeader>
                <CardTitle>Service-Ranking</CardTitle>
                <CardDescription>
                  Detaillierte Übersicht der Service-Beliebtheit
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!servicePopularity || servicePopularity.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Buchungsdaten verfügbar
                  </div>
                ) : (
                  <div className="space-y-4">
                    {servicePopularity.map((service, index) => (
                      <div key={service.serviceId} className="flex items-center">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{service.serviceName}</p>
                            <p className="text-sm text-muted-foreground">{service.bookingCount} Buchungen</p>
                          </div>
                        </div>
                        <div className="w-32">
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${(service.bookingCount / (servicePopularity[0]?.bookingCount || 1)) * 100}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
